import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Loader2, Calendar, User, CheckCircle, X, Phone, Mail, RefreshCw, CalendarPlus, CalendarCheck, CalendarX } from 'lucide-react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { tareasDespachoAPI, contactosAPI } from '../../api';
import { toast } from '../../hooks/use-toast';
import ExpedienteSearch from '../ExpedienteSearch';
import { PRIORIDADES, CANALES_ENTRADA, ESTADOS_CITA } from './constants';

const CitaAbogadosTab = ({ usuarios, config }) => {
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterPrioridad, setFilterPrioridad] = useState('');
  const [filterCanal, setFilterCanal] = useState('');
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [editingCita, setEditingCita] = useState(null);
  const [selectedCita, setSelectedCita] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [abogadosDisponibles, setAbogadosDisponibles] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [contactoSearch, setContactoSearch] = useState('');
  const [contactoSearchResults, setContactoSearchResults] = useState([]);
  const [contactoSearchLoading, setContactoSearchLoading] = useState(false);
  const [contactoPopoverOpen, setContactoPopoverOpen] = useState(false);
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [selectedContacto, setSelectedContacto] = useState(null);
  
  const [form, setForm] = useState({
    canal_entrada: 'telefono',
    cliente_nombre: '',
    cliente_apellidos: '',
    cliente_telefono: '',
    cliente_email: '',
    descripcion_asunto: '',
    propuesta_fechas_cliente: '',
    fecha_cita: '',
    hora_cita: '',
    duracion_estimada: 30,
    prioridad: 'media',
    abogado_id: '',
    abogado_nombre: '',
    notas_internas: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadCitas();
  }, [filterEstado, filterPrioridad, filterCanal]);

  const loadData = async () => {
    try {
      const [abogadosRes, contactosRes] = await Promise.all([
        tareasDespachoAPI.citasAbogados.getAbogados(),
        contactosAPI.getAll({ limit: 1000 })
      ]);
      setAbogadosDisponibles(abogadosRes.data || []);
      setContactos(contactosRes.data?.data || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    }
  };

  const loadCitas = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterEstado && filterEstado !== 'all') params.estado = filterEstado;
      if (filterPrioridad && filterPrioridad !== 'all') params.prioridad = filterPrioridad;
      if (filterCanal && filterCanal !== 'all') params.canal = filterCanal;
      if (search) params.search = search;
      
      const res = await tareasDespachoAPI.citasAbogados.getAll(params);
      setCitas(res.data || []);
    } catch (error) {
      console.error('Error cargando citas:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las citas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      canal_entrada: 'telefono',
      cliente_nombre: '',
      cliente_apellidos: '',
      cliente_telefono: '',
      cliente_email: '',
      descripcion_asunto: '',
      propuesta_fechas_cliente: '',
      fecha_cita: '',
      hora_cita: '',
      duracion_estimada: 30,
      prioridad: 'media',
      abogado_id: '',
      abogado_nombre: '',
      notas_internas: ''
    });
    setSelectedExpediente(null);
    setSelectedContacto(null);
    setEditingCita(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (cita) => {
    const fechaCita = cita.fecha_cita ? new Date(cita.fecha_cita) : null;
    setForm({
      canal_entrada: cita.canal_entrada || 'telefono',
      cliente_nombre: cita.cliente_nombre || '',
      cliente_apellidos: cita.cliente_apellidos || '',
      cliente_telefono: cita.cliente_telefono || '',
      cliente_email: cita.cliente_email || '',
      descripcion_asunto: cita.descripcion_asunto || '',
      propuesta_fechas_cliente: cita.propuesta_fechas_cliente || '',
      fecha_cita: fechaCita ? fechaCita.toISOString().split('T')[0] : '',
      hora_cita: fechaCita ? fechaCita.toTimeString().slice(0, 5) : '',
      duracion_estimada: cita.duracion_estimada || 30,
      prioridad: cita.prioridad || 'media',
      abogado_id: cita.abogado_id?.toString() || '',
      abogado_nombre: cita.abogado_nombre || '',
      notas_internas: cita.notas_internas || ''
    });
    setSelectedExpediente(cita.expediente_id ? { id: cita.expediente_id, numero_expediente: cita.expediente_numero } : null);
    setSelectedContacto(cita.contacto_id ? { id: cita.contacto_id } : null);
    setEditingCita(cita);
    setIsDialogOpen(true);
  };

  const handleContactoSelect = (contacto) => {
    setContactoPopoverOpen(false);
    setContactoSearch('');
    setSelectedContacto(contacto);
    if (contacto) {
      setForm({
        ...form,
        cliente_nombre: contacto.nombre || '',
        cliente_apellidos: contacto.primer_apellido ? contacto.primer_apellido + ' ' + (contacto.segundo_apellido || '') : '',
        cliente_telefono: contacto.telefono || '',
        cliente_email: contacto.email || ''
      });
    }
  };


  const searchContactos = async (query) => {
    if (!query || query.length < 2) {
      setContactoSearchResults([]);
      return;
    }
    try {
      setContactoSearchLoading(true);
      const res = await contactosAPI.getAll({ search: query, limit: 20 });
      const data = res.data?.data || res.data || [];
      setContactoSearchResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error buscando contactos:", error);
      setContactoSearchResults([]);
    } finally {
      setContactoSearchLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (contactoSearch) {
        searchContactos(contactoSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contactoSearch]);
  const handleAbogadoChange = (value) => {
    // Value puede ser "nombre:NombreAbogado" o "id:123"
    if (value.startsWith('nombre:')) {
      const nombre = value.replace('nombre:', '');
      setForm({ ...form, abogado_id: '', abogado_nombre: nombre });
    } else if (value.startsWith('id:')) {
      const id = value.replace('id:', '');
      const abogado = abogadosDisponibles.find(a => a.id?.toString() === id);
      setForm({ ...form, abogado_id: id, abogado_nombre: abogado?.nombre || '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente_nombre || !form.descripcion_asunto) {
      toast({ title: 'Error', description: 'Nombre del cliente y descripción del asunto son obligatorios', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      // Construir fecha_cita
      let fechaCita = null;
      if (form.fecha_cita && form.hora_cita) {
        fechaCita = `${form.fecha_cita}T${form.hora_cita}:00`;
      }
      
      const data = {
        canal_entrada: form.canal_entrada,
        contacto_id: selectedContacto?.id || null,
        cliente_nombre: form.cliente_nombre,
        cliente_apellidos: form.cliente_apellidos || null,
        cliente_telefono: form.cliente_telefono || null,
        cliente_email: form.cliente_email || null,
        expediente_id: selectedExpediente?.id || null,
        expediente_numero: selectedExpediente?.numero_expediente || null,
        descripcion_asunto: form.descripcion_asunto,
        propuesta_fechas_cliente: form.propuesta_fechas_cliente || null,
        fecha_cita: fechaCita,
        duracion_estimada: parseInt(form.duracion_estimada) || 30,
        prioridad: form.prioridad,
        abogado_id: form.abogado_id ? parseInt(form.abogado_id) : null,
        abogado_nombre: form.abogado_nombre || null,
        notas_internas: form.notas_internas || null
      };
      
      if (editingCita) {
        await tareasDespachoAPI.citasAbogados.update(editingCita.id, data);
        toast({ title: 'Cita actualizada' });
      } else {
        const res = await tareasDespachoAPI.citasAbogados.create(data);
        const citaCreada = res.data;
        let descripcionToast = citaCreada?.evento_id
          ? 'Evento creado en agenda.'
          : 'ATENCION: no se ha podido crear el evento en la agenda.';
        if (citaCreada?.email_enviado) {
          descripcionToast += ' Email enviado al abogado.';
        } else if (data.abogado_id) {
          descripcionToast += ' No se pudo enviar email al abogado.';
        }
        toast({ title: 'Cita creada correctamente', description: descripcionToast });
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadCitas();
    } catch (error) {
      console.error('Error guardando cita:', error);
      toast({ title: 'Error', description: 'No se pudo guardar la cita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cita) => {
    if (!window.confirm('¿Eliminar esta cita?')) return;
    try {
      await tareasDespachoAPI.citasAbogados.delete(cita.id);
      toast({ title: 'Cita eliminada' });
      loadCitas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  const handleCambiarEstado = async (cita, nuevoEstado) => {
    try {
      await tareasDespachoAPI.citasAbogados.cambiarEstado(cita.id, nuevoEstado);
      toast({ title: `Estado cambiado a: ${ESTADOS_CITA[nuevoEstado]?.nombre}` });
      loadCitas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo cambiar el estado', variant: 'destructive' });
    }
  };

  // [v140] repara las citas que se quedaron sin evento en el calendario
  const sincronizarAgenda = async () => {
    setSincronizando(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/tareas-despacho/citas-abogados/sincronizar-agenda`
      );
      const d = res.data || {};
      toast({
        title: 'Agenda sincronizada',
        description: `${d.eventos_creados || 0} cita(s) puestas en el calendario, ${d.eventos_actualizados || 0} ya estaban.`
      });
      loadCitas();
    } catch (error) {
      console.error('Error sincronizando agenda:', error);
      toast({ title: 'Error', description: 'No se pudo sincronizar la agenda', variant: 'destructive' });
    } finally {
      setSincronizando(false);
    }
  };

  const Icon = config.icon;

  return (
    <>
      {/* Header */}
      <Card className={`${config.lightBg} border-l-4 ${config.borderColor}`}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Icon className={`w-8 h-8 ${config.textColor}`} />
              <div>
                <h2 className="text-xl font-bold text-gray-900">{config.nombre}</h2>
                <p className="text-sm text-gray-600">Peticiones de cita/visita con abogados</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={sincronizarAgenda}
                disabled={sincronizando}
                title="Pone en el calendario las citas que se quedaron sin evento"
                data-testid="citas-sincronizar-agenda-btn"
              >
                {sincronizando
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <CalendarCheck className="w-4 h-4 mr-2" />}
                {sincronizando ? 'Sincronizando...' : 'Sincronizar agenda'}
              </Button>
              <Button onClick={handleOpenNew} className={`${config.bgColor} hover:opacity-90`} data-testid="citas-nueva-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Cita
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por cliente, asunto, expediente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadCitas()}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterEstado} onValueChange={setFilterEstado}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(ESTADOS_CITA).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPrioridad} onValueChange={setFilterPrioridad}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Prioridad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(PRIORIDADES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCanal} onValueChange={setFilterCanal}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Canal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(CANALES_ENTRADA).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={loadCitas}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de citas */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : citas.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CalendarPlus className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay citas registradas</p>
            </div>
          ) : (
            <div className="divide-y">
              {(citas||[]).map((cita) => {
                const CanalIcon = CANALES_ENTRADA[cita.canal_entrada]?.icon || Phone;
                return (
                  <div 
                    key={cita.id}
                    className={`p-4 hover:bg-gray-50 transition-colors border-l-4 ${
                      cita.prioridad === 'urgente' ? 'border-l-red-500 bg-red-50/30' :
                      cita.prioridad === 'alta' ? 'border-l-orange-500' :
                      'border-l-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Encabezado */}
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <Badge className={CANALES_ENTRADA[cita.canal_entrada]?.color || 'bg-gray-100'}>
                            <CanalIcon className="w-3 h-3 mr-1" />
                            {CANALES_ENTRADA[cita.canal_entrada]?.nombre || cita.canal_entrada}
                          </Badge>
                          <Badge className={ESTADOS_CITA[cita.estado]?.color || 'bg-gray-100'}>
                            {ESTADOS_CITA[cita.estado]?.nombre || cita.estado}
                          </Badge>
                          <Badge className={PRIORIDADES[cita.prioridad]?.color}>
                            {PRIORIDADES[cita.prioridad]?.nombre}
                          </Badge>
                          {cita.expediente_numero && (
                            <Badge variant="outline" className="text-xs">
                              {cita.expediente_numero}
                            </Badge>
                          )}
                          {!cita.evento_id && (
                            <Badge
                              className="bg-amber-100 text-amber-800 text-xs"
                              title="Esta cita no esta en el calendario. Guardala otra vez o pulsa 'Sincronizar agenda'."
                              data-testid={`cita-sin-agenda-${cita.id}`}
                            >
                              <CalendarX className="w-3 h-3 mr-1" />
                              Sin agenda
                            </Badge>
                          )}
                        </div>
                        
                        {/* Cliente */}
                        <h3 className="font-semibold text-gray-900">
                          {cita.cliente_nombre} {cita.cliente_apellidos || ''}
                        </h3>
                        
                        {/* Contacto */}
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          {cita.cliente_telefono && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {cita.cliente_telefono}
                            </span>
                          )}
                          {cita.cliente_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {cita.cliente_email}
                            </span>
                          )}
                        </div>
                        
                        {/* Asunto */}
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{cita.descripcion_asunto}</p>
                        
                        {/* Fecha cita y abogado */}
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          {cita.fecha_cita && (
                            <span className="flex items-center gap-1 text-green-600 font-medium">
                              <Calendar className="w-4 h-4" />
                              {new Date(cita.fecha_cita).toLocaleString('es-ES', { 
                                dateStyle: 'short', 
                                timeStyle: 'short' 
                              })}
                            </span>
                          )}
                          {cita.abogado_nombre && (
                            <span className="flex items-center gap-1 text-indigo-600">
                              <User className="w-4 h-4" />
                              {cita.abogado_nombre}
                            </span>
                          )}
                          {cita.email_enviado && (
                            <span className="text-green-500 text-xs flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Email enviado
                            </span>
                          )}
                        </div>
                        
                        {/* Propuesta fechas */}
                        {cita.propuesta_fechas_cliente && !cita.fecha_cita && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-700">
                            <strong>Propuesta cliente:</strong> {cita.propuesta_fechas_cliente}
                          </div>
                        )}
                      </div>
                      
                      {/* Acciones */}
                      <div className="flex items-center gap-1">
                        <Select 
                          value={cita.estado} 
                          onValueChange={(v) => handleCambiarEstado(cita, v)}
                        >
                          <SelectTrigger className="w-[130px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(ESTADOS_CITA).map(([key, val]) => (
                              <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(cita)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(cita)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog crear/editar */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5" />
              {editingCita ? 'Editar Cita' : 'Nueva Petición de Cita'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Canal de entrada */}
            <div>
              <Label>Canal de entrada *</Label>
              <Select value={form.canal_entrada} onValueChange={(v) => setForm({ ...form, canal_entrada: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CANALES_ENTRADA).map(([key, val]) => {
                    const CanalIcon = val.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <CanalIcon className="w-4 h-4" />
                          {val.nombre}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Buscar contacto existente */}
            <div>
              <Label>Buscar contacto existente (opcional)</Label>
              <Popover open={contactoPopoverOpen} onOpenChange={setContactoPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={contactoPopoverOpen} className="w-full justify-between font-normal">
                    {selectedContacto ? `${selectedContacto.nombre} ${selectedContacto.primer_apellido ? selectedContacto.primer_apellido + " " + (selectedContacto.segundo_apellido || "") : ""}` : "Buscar contacto..."}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="Escribe nombre, teléfono o email..." value={contactoSearch} onValueChange={setContactoSearch} />
                    <CommandList>
                      {contactoSearchLoading && <div className="flex items-center justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /><span className="ml-2 text-sm text-gray-500">Buscando...</span></div>}
                      {!contactoSearchLoading && contactoSearch.length >= 2 && contactoSearchResults.length === 0 && <CommandEmpty>No se encontraron contactos</CommandEmpty>}
                      {!contactoSearchLoading && contactoSearch.length < 2 && <div className="py-4 text-center text-sm text-gray-500">Escribe al menos 2 caracteres para buscar</div>}
                      {contactoSearchResults.length > 0 && <CommandGroup heading="Contactos encontrados">{contactoSearchResults.map((c) => <CommandItem key={c.id} value={c.id.toString()} onSelect={() => handleContactoSelect(c)} className="cursor-pointer"><div className="flex flex-col"><span className="font-medium">{c.nombre} {c.primer_apellido ? c.primer_apellido + " " + (c.segundo_apellido || "") : ""}</span><span className="text-xs text-gray-500">{c.telefono || ""} {c.telefono && c.email ? " | " : ""} {c.email || ""}</span></div></CommandItem>)}</CommandGroup>}
                    </CommandList>
                  </Command>
                  {selectedContacto && <div className="border-t p-2"><Button variant="ghost" size="sm" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleContactoSelect(null)}><X className="w-4 h-4 mr-2" />Limpiar selección</Button></div>}
                </PopoverContent>
              </Popover>
              {selectedContacto && <p className="text-xs text-green-600 mt-1">✓ Contacto seleccionado: {selectedContacto.nombre} {selectedContacto.primer_apellido ? selectedContacto.primer_apellido + " " + (selectedContacto.segundo_apellido || "") : ""}</p>}
            </div>

            
            {/* Datos del cliente */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input 
                  value={form.cliente_nombre}
                  onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Apellidos</Label>
                <Input 
                  value={form.cliente_apellidos}
                  onChange={(e) => setForm({ ...form, cliente_apellidos: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono</Label>
                <Input 
                  value={form.cliente_telefono}
                  onChange={(e) => setForm({ ...form, cliente_telefono: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={form.cliente_email}
                  onChange={(e) => setForm({ ...form, cliente_email: e.target.value })}
                />
              </div>
            </div>
            
            {/* Expediente relacionado */}
            <div>
              <Label>Expediente relacionado (opcional)</Label>
              <ExpedienteSearch
                value={selectedExpediente}
                onChange={setSelectedExpediente}
              />
            </div>
            
            {/* Descripción del asunto */}
            <div>
              <Label>Descripción del asunto *</Label>
              <Textarea
                value={form.descripcion_asunto}
                onChange={(e) => setForm({ ...form, descripcion_asunto: e.target.value })}
                rows={3}
                placeholder="¿Qué asunto quiere tratar el cliente?"
                required
              />
            </div>
            
            {/* Propuesta de fechas del cliente */}
            <div>
              <Label>Propuesta de fechas del cliente</Label>
              <Textarea
                value={form.propuesta_fechas_cliente}
                onChange={(e) => setForm({ ...form, propuesta_fechas_cliente: e.target.value })}
                rows={2}
                placeholder="Ej: Lunes por la mañana, martes a partir de las 16h..."
              />
            </div>
            
            {/* Fecha y hora asignada */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Fecha cita</Label>
                <Input 
                  type="date"
                  value={form.fecha_cita}
                  onChange={(e) => setForm({ ...form, fecha_cita: e.target.value })}
                />
              </div>
              <div>
                <Label>Hora</Label>
                <Input 
                  type="time"
                  value={form.hora_cita}
                  onChange={(e) => setForm({ ...form, hora_cita: e.target.value })}
                />
              </div>
              <div>
                <Label>Duración (min)</Label>
                <Select 
                  value={form.duracion_estimada.toString()} 
                  onValueChange={(v) => setForm({ ...form, duracion_estimada: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">1 hora</SelectItem>
                    <SelectItem value="90">1h 30min</SelectItem>
                    <SelectItem value="120">2 horas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Prioridad y Abogado */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prioridad</Label>
                <Select value={form.prioridad} onValueChange={(v) => setForm({ ...form, prioridad: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORIDADES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Abogado asignado</Label>
                <Select 
                  value={form.abogado_id ? `id:${form.abogado_id}` : form.abogado_nombre ? `nombre:${form.abogado_nombre}` : ''}
                  onValueChange={handleAbogadoChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar abogado..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(abogadosDisponibles||[]).map((ab, idx) => (
                      <SelectItem 
                        key={ab.id || `pred-${idx}`} 
                        value={ab.id ? `id:${ab.id}` : `nombre:${ab.nombre}`}
                      >
                        {ab.nombre} {ab.es_usuario ? '(Usuario)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Notas internas */}
            <div>
              <Label>Notas internas</Label>
              <Textarea
                value={form.notas_internas}
                onChange={(e) => setForm({ ...form, notas_internas: e.target.value })}
                rows={2}
                placeholder="Notas para uso interno del despacho..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className={config.bgColor}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingCita ? 'Guardar cambios' : 'Crear Cita'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};


export default CitaAbogadosTab;
